import { action, makeObservable, observable } from 'mobx'
import i18n from 'i18next'

export const ENTITY_TYPES = ['division', 'portfolio', 'product']

export const STATUS_FILTER_ALL = 'all'
export const STATUS_FILTER_ENABLED = 'enabled'
export const STATUS_FILTER_DISABLED = 'disabled'

const emptyTab = () => ({
    items: [],
    configurable: true,
    loading: false,
    loaded: false,
    error: null,
})

class PresentationAccessAdminStore {
    appStore

    @observable activeTab = 'division'

    @observable clientLevel = null

    @observable clientLevelLoading = true

    @observable clientLevelSaving = false

    @observable clientLevelError = null

    @observable division = emptyTab()

    @observable portfolio = emptyTab()

    @observable product = emptyTab()

    @observable filters = {
        division: { id: '', name: '', status: STATUS_FILTER_ALL },
        portfolio: { id: '', name: '', status: STATUS_FILTER_ALL },
        product: { id: '', name: '', status: STATUS_FILTER_ALL },
    }

    constructor(appStore) {
        makeObservable(this)
        this.appStore = appStore
    }

    endpointFor = (entityType) => `/intranet/presentationaccess/${entityType}`

    @action
    init = async () => {
        await this.getClientLevel()
        await this.activateTab(this.activeTab)
    }

    @action
    getClientLevel = async () => {
        const { appStore } = this
        this.clientLevelLoading = true
        this.clientLevelError = null

        try {
            const { level } = await appStore.http.get('/intranet/presentationaccess/client')
            this.clientLevel = level
        } catch (e) {
            console.error(e)
            this.clientLevelError = i18n.t('intranet.presentationAccessAdmin.clientLevel.loadError')
            appStore.addNotification(null, this.clientLevelError, 'error')
        }

        this.clientLevelLoading = false
    }

    @action
    changeClientLevel = async ({ value }) => {
        const { appStore } = this
        const previousLevel = this.clientLevel
        this.clientLevelSaving = true
        this.clientLevelError = null

        try {
            const response = await appStore.http.put(
                '/intranet/presentationaccess/client',
                JSON.stringify({ level: value }),
            )
            this.clientLevel = response.level
            ENTITY_TYPES.forEach((entityType) => this.resetTab(entityType))
            await this.activateTab(this.activeTab, true)
        } catch (e) {
            console.error(e)
            this.clientLevel = previousLevel
            this.clientLevelError = i18n.t('intranet.presentationAccessAdmin.clientLevel.updateError')
            appStore.addNotification(null, this.clientLevelError, 'error')
        }

        this.clientLevelSaving = false
    }

    @action
    resetTab = (entityType) => {
        this[entityType] = emptyTab()
    }

    @action
    activateTab = async (entityType, forceReload = false) => {
        this.activeTab = entityType
        const tab = this[entityType]

        if (tab.loading) return
        if (tab.loaded && !forceReload) return

        await this.loadTab(entityType)
    }

    @action
    loadTab = async (entityType) => {
        const { appStore } = this
        const tab = this[entityType]
        tab.loading = true
        tab.error = null

        try {
            const { configurable, items } = await appStore.http.get(this.endpointFor(entityType))
            tab.items = items
            tab.configurable = configurable
            tab.loaded = true
        } catch (e) {
            console.error(e)
            tab.error = i18n.t('intranet.presentationAccessAdmin.tabs.loadError')
            appStore.addNotification(null, tab.error, 'error')
        }

        tab.loading = false
    }

    changeFilter = (entityType, field, value) => {
        this.filters[entityType][field] = value
    }

    getFilteredItems = (entityType) => {
        const { items } = this[entityType]
        const {
            id: idFilter, name: nameFilter, status: statusFilter,
        } = this.filters[entityType]

        const trimmedId = idFilter.trim()
        const trimmedName = nameFilter.trim().toLowerCase()

        return items.filter((item) => {
            const idMatches = !trimmedId || String(item.id).includes(trimmedId)
            const nameMatches = !trimmedName || item.name.toLowerCase().includes(trimmedName)
            const statusMatches = statusFilter === STATUS_FILTER_ALL
                || (statusFilter === STATUS_FILTER_ENABLED && item.effectiveEnabled)
                || (statusFilter === STATUS_FILTER_DISABLED && !item.effectiveEnabled)

            return idMatches && nameMatches && statusMatches
        })
    }

    @action
    toggleEntity = async (entityType, entityId, enabled) => {
        const { appStore } = this
        const tab = this[entityType]
        const item = tab.items.find(({ id }) => id === entityId)

        if (!item || !item.editable) return

        try {
            const updated = await appStore.http.put(
                `${this.endpointFor(entityType)}/${entityId}`,
                JSON.stringify({ enabled }),
            )
            const index = tab.items.findIndex(({ id }) => id === entityId)
            if (index > -1) tab.items[index] = updated
        } catch (e) {
            console.error(e)
            appStore.addNotification(
                null,
                i18n.t('intranet.presentationAccessAdmin.tabs.toggleError'),
                'error',
            )
        }
    }
}

export default PresentationAccessAdminStore
